import forEach from "lodash/forEach";
import range from "lodash/range";
import zipObject from "lodash/zipObject";
import { ActivityRow } from "PFApp/activities/engagement_blueprint/components/availability_table";
import {
  FIELD_AVAILABILITY_MODE_KEY,
  FIELD_CONVERTED_AVAILABILITY_RANGES_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import {
  ConvertedRangesFieldKey,
  RoleFormKey,
  RolesFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { ManageRolesMultiForm } from "PFApp/types/activity_form/manage_roles_form_schema";
import { Inline } from "PFComponents/containers";
import { AvailabilityMode, StandardRangeV2, Subtemplate } from "PFCore/types";
import { memo, RefCallback, useCallback, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AvailabilityTable } from "../../engagement_blueprint/components/availability_table";
import { useManageRolesActionsColumn } from "../hooks/use_manage_roles_actions_column";
import { useVisibilityLimitedCalendarDuration } from "../hooks/use_visibility_limited_calendar_duration";
import { ManageRolesActionType } from "../manage_roles.types";
import css from "../manage_roles_page.module.scss";
import { ManageRolesForm } from "./manage_roles_form";

type ManageRolesTablesProps = {
  rolesFormKey: RolesFormKey;
  subtemplate: Subtemplate | null;
  leftPanelRef: RefCallback<HTMLDivElement>;
  onEditAvailability: (roleFormKey: RoleFormKey) => void;
  onPublish: (roleFormKey: RoleFormKey) => void;
  onFillAndBook: (roleFormKey: RoleFormKey) => void;
};

/**
 * Memoized component holding the two expensive tables.
 * Isolated from EditAvailabilitySidePanel state so opening the panel
 * does not re-render ManageRolesForm or AvailabilityTable.
 */
export const ManageRolesTables = memo(
  ({
    rolesFormKey,
    subtemplate,
    leftPanelRef,
    onEditAvailability,
    onPublish,
    onFillAndBook
  }: ManageRolesTablesProps) => {
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const { setValue } = useFormContext<ManageRolesMultiForm>();
    const calendarDuration = useVisibilityLimitedCalendarDuration();

    const rolesLength = useWatch<ManageRolesMultiForm, RolesFormKey, unknown, number>({
      name: rolesFormKey,
      compute: (roles = []) => roles.length
    });

    const roleFormKeys = useMemo<RoleFormKey[]>(
      () => range(rolesLength).map((index) => `${rolesFormKey}.${index}` as RoleFormKey),
      [rolesLength, rolesFormKey]
    );

    const rangesValues = useWatch<ManageRolesMultiForm>({
      name: roleFormKeys.map(
        (key) => `${key}.${FIELD_CONVERTED_AVAILABILITY_RANGES_KEY}`
      ) as ConvertedRangesFieldKey[]
    }) as StandardRangeV2[][];

    const availabilityModes = useWatch<ManageRolesMultiForm>({
      name: roleFormKeys.map(
        (key) => `${key}.${FIELD_AVAILABILITY_MODE_KEY}`
      ) as `${RoleFormKey}.${typeof FIELD_AVAILABILITY_MODE_KEY}`[]
    }) as AvailabilityMode[];

    const availabilityTableValue = useMemo(
      () => zipObject(roleFormKeys, rangesValues) as Record<RoleFormKey, StandardRangeV2[]>,
      [roleFormKeys, rangesValues]
    );

    const disabledRoleFormKeys = useMemo(
      () =>
        roleFormKeys.filter(
          (_, index) => availabilityModes[index] !== AvailabilityMode.PerWeek
        ) as RoleFormKey[],
      [roleFormKeys, availabilityModes]
    );

    const handleAvailabilityChange = useCallback(
      (availability: Record<RoleFormKey, StandardRangeV2[]>) => {
        forEach(availability, (availabilityRules, roleFormKey) => {
          const fieldPath =
            `${roleFormKey}.${FIELD_CONVERTED_AVAILABILITY_RANGES_KEY}` as ConvertedRangesFieldKey;
          setValue(fieldPath, availabilityRules, { shouldDirty: true });
        });
      },
      [setValue]
    );

    const handleActionsChange = useCallback(
      (roleFormKey: RoleFormKey, action: ManageRolesActionType) => {
        if (action === ManageRolesActionType.EditAvailability) {
          onEditAvailability(roleFormKey);
        } else if (action === ManageRolesActionType.Publish) {
          onPublish(roleFormKey);
        } else if (action === ManageRolesActionType.FillAndBook) {
          onFillAndBook(roleFormKey);
        }
      },
      [onEditAvailability, onPublish, onFillAndBook]
    );

    const generateActionsColumn = useManageRolesActionsColumn<ActivityRow>({
      roleFormKeys,
      onChange: handleActionsChange
    });

    return (
      <Inline className={css.accordionContent} gap={null}>
        <div
          ref={leftPanelRef}
          className={css.leftPanel}
          data-qa-id={`ManageRoles.${subtemplate?.key}.LeftPanel`}
        >
          <ManageRolesForm subtemplate={subtemplate} rolesFormKey={rolesFormKey} />
        </div>
        <div
          ref={rightPanelRef}
          className={css.rightPanel}
          data-qa-id={`ManageRoles.${subtemplate?.key}.RightPanel`}
        >
          <AvailabilityTable
            key={roleFormKeys.join(",")}
            roleFormKeys={roleFormKeys}
            value={availabilityTableValue}
            onRowsChange={handleAvailabilityChange}
            generateActionsColumn={generateActionsColumn}
            scrollableContainerRef={rightPanelRef}
            calendarDuration={calendarDuration}
            disabledRoleFormKeys={disabledRoleFormKeys}
          />
        </div>
      </Inline>
    );
  }
);

ManageRolesTables.displayName = "ManageRolesTables";
